import { memo } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkIcon from '@mui/icons-material/Link';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { getChannelUrlByPlatform } from 'root/src/shared/creator/helpers/creatorProfile.helpers';
import { PlatformIcon } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { useSettingsDialog } from 'root/src/client/v2/profile/features/ProfileSettingsDialog/settingsDialog.context';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { Button } from 'root/src/client/v2/common/components/ui/button';
const ProfileCard = memo(({ label, token, badge }) => (<div className="w-full">
		<p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
		<a href={getChannelUrlByPlatform({ platform: token.platform, mnemonicId: token.mnemonicId })} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 rounded-xl border border-solid border-white/[0.08] bg-white/[0.03] px-3 py-2.5 transition-colors hover:border-white/20">
			<PlatformIcon platform={token.platform} size="1x"/>
			<span className="flex-1 truncate text-sm font-medium text-foreground">{token.displayName}</span>
			{badge}
		</a>
	</div>));
export const RedesignPreferredPlatformReachedMaxDaresModal = memo(({ onClose, onSubmit, platform }) => {
    const [showPlatformAdjustedModal, setShowPlatformAdjustedModal] = useLocalStorage('showPlatformAdjustedModal', true);
    const { tokens = [] } = useUserCreatorData();
    const { openSettings } = useSettingsDialog();
    const targetToken = tokens.find(token => token.platform === platform);
    const preferredToken = tokens.find(token => token.isPreferred);
    const onUpdatePreferred = () => {
        openSettings('channels');
        toast('Here you can change your preferred platform');
        onClose();
    };
    return (<RedesignModalShell className="sm:max-w-lg">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-amber/30 bg-neon-amber/10">
					<WarningAmberIcon sx={{ fontSize: 24 }} className="text-neon-amber"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Heads up</h2>
				<p className="text-sm text-muted-foreground">
					Your preferred profile has reached its dare limit for this drop, so this dare will be assigned to another connected profile.
				</p>
			</div>

			<div className="flex w-full flex-col gap-3">
				{preferredToken && (<ProfileCard label="Preferred profile" token={preferredToken} badge={(<span className="shrink-0 rounded-full border border-solid border-neon-amber/30 bg-neon-amber/10 px-2 py-0.5 text-[10px] font-semibold text-neon-amber">
								Limit reached
							</span>)}/>)}
				{targetToken && <ProfileCard label="This dare goes to" token={targetToken}/>}
			</div>

			<p className="w-full text-center text-xs text-muted-foreground">
				Each connected profile has its own dare limit based on its average viewership.
			</p>

			<Button variant="outline" size="sm" onClick={onUpdatePreferred} className="gap-1.5 rounded-lg text-xs">
				<LinkIcon sx={{ fontSize: 16 }}/>
				Connected channels
			</Button>

			<label htmlFor="pp-dismiss" className="flex cursor-pointer select-none items-center gap-2 text-xs text-muted-foreground">
				<Checkbox id="pp-dismiss" checked={!showPlatformAdjustedModal} onCheckedChange={value => setShowPlatformAdjustedModal(!value)}/>
				Don&apos;t show this again
			</label>

			<div className="flex w-full gap-2">
				<Button variant="secondary" onClick={onClose} className="flex-1 rounded-lg text-sm">Cancel</Button>
				<Button variant="brand" onClick={onSubmit} className="flex-1 rounded-lg text-sm">Continue</Button>
			</div>
		</RedesignModalShell>);
});
// Redesign of the preferred-platform max-dares warning. Resolves true (continue) / false (cancel).
export const useRedesignPreferredPlatformReachedMaxDaresModal = () => {
    const modal = useRedesignModal();
    return (platform) => new Promise((resolve) => {
        modal.show(<RedesignPreferredPlatformReachedMaxDaresModal platform={platform} onClose={() => { modal.hide(); resolve(false); }} onSubmit={() => { modal.hide(); resolve(true); }}/>, { closeable: false });
    });
};
